import z from "zod";

import { DOMAIN_PATTERN } from "../constants/app";
import { FIELD_MAX_LENGTH } from "../constants/field-length";
import { emailSchema, passwordSchema } from "./common";
import { requiredString } from "./field-builders";
import type { TAnyTranslationFunction } from "./types";

export const signinSchema = (t: TAnyTranslationFunction) =>
  z.object({
    email: emailSchema(t),
    password: z.string().min(1, t("auth:password.error.required")),
  });

export const signupSchema = (t: TAnyTranslationFunction) =>
  z.object({
    firstName: requiredString({
      requiredMessage: t("auth:first_name.error.required"),
      maxLength: FIELD_MAX_LENGTH.STANDARD,
      maxLengthMessage: t("auth:first_name.error.max_length"),
    }),
    lastName: requiredString({
      requiredMessage: t("auth:last_name.error.required"),
      maxLength: FIELD_MAX_LENGTH.STANDARD,
      maxLengthMessage: t("auth:last_name.error.max_length"),
    }),
    email: emailSchema(t),
    password: passwordSchema(t),
  });

export const forgotPasswordSchema = (t: TAnyTranslationFunction) =>
  z.object({
    email: emailSchema(t),
  });

export const resetPasswordSchema = (t: TAnyTranslationFunction) =>
  z
    .object({
      password: passwordSchema(t),
      confirmPassword: z.string().min(1, t("auth:password.error.required")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("auth:confirm_password.error.mismatch"),
    });

export const acceptInviteSchema = (t: TAnyTranslationFunction) =>
  z
    .object({
      firstName: requiredString({
        requiredMessage: t("auth:first_name.error.required"),
        maxLength: FIELD_MAX_LENGTH.STANDARD,
        maxLengthMessage: t("auth:first_name.error.max_length"),
      }),
      lastName: requiredString({
        requiredMessage: t("auth:last_name.error.required"),
        maxLength: FIELD_MAX_LENGTH.STANDARD,
        maxLengthMessage: t("auth:last_name.error.max_length"),
      }),
      password: passwordSchema(t),
      confirmPassword: z.string().min(1, t("auth:password.error.required")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("auth:confirm_password.error.mismatch"),
    });

export type TAcceptInviteForm = z.infer<ReturnType<typeof acceptInviteSchema>>;

export const ssoSchema = (t: TAnyTranslationFunction) =>
  z.object({
    email: requiredString({
      requiredMessage: t("auth:email.error.required"),
    }).refine(
      (val) => {
        // NOTE: Allow email or domain
        if (val.includes("@")) {
          return z.string().email().safeParse(val).success;
        }
        return DOMAIN_PATTERN.test(val);
      },
      {
        message: t("auth:email.error.invalid"),
      },
    ),
  });
